import { apiFetch } from "./client";
import { canonicalStockyPublicBigIntId } from "./stocky_public_ids";

/** 店の仕入税率1行（backend `routers/tax_rates._serialize` と 1:1）。 */
export interface ShopTaxRate {
  /** API境界でcanonical化済みの正のBIGINT decimal string。 */
  id: string;
  name: string;
  /** 小数文字列（"0.1000" = 10%）。表示は `rateToPercent` で変換する。 */
  rate: string;
  is_default: boolean;
  sort_order: number;
}

type TaxRateWire = Omit<ShopTaxRate, "id"> & { id: unknown };

function normalizeTaxRate(rate: TaxRateWire): ShopTaxRate {
  return { ...rate, id: canonicalStockyPublicBigIntId(rate.id) };
}

function taxRatePathId(id: string): string {
  // Legacy number compatibility belongs only to response normalization.  A
  // mutation target must already be the canonical string held by the UI.
  if (typeof id !== "string") {
    throw new TypeError("tax rate id must be a canonical decimal string");
  }
  return encodeURIComponent(canonicalStockyPublicBigIntId(id));
}

export async function listTaxRates(): Promise<{ tax_rates: ShopTaxRate[] }> {
  const response = await apiFetch<{ tax_rates: TaxRateWire[] }>(
    "/api/tax-rates",
  );
  return { tax_rates: response.tax_rates.map(normalizeTaxRate) };
}

export interface TaxRateCreatePayload {
  name: string;
  rate: string;
  is_default?: boolean;
}

export async function createTaxRate(
  body: TaxRateCreatePayload,
): Promise<ShopTaxRate> {
  const response = await apiFetch<TaxRateWire>("/api/tax-rates", {
    method: "POST",
    body,
  });
  return normalizeTaxRate(response);
}

export interface TaxRateUpdatePayload {
  name?: string;
  rate?: string;
  is_default?: boolean;
}

export function updateTaxRate(
  id: string,
  body: TaxRateUpdatePayload,
): Promise<ShopTaxRate> {
  return apiFetch<TaxRateWire>(`/api/tax-rates/${taxRatePathId(id)}`, {
    method: "PUT",
    body,
  }).then(normalizeTaxRate);
}

/** アーカイブ（論理削除）。参照している商品は既定の税率へ倒れる。 */
export async function deleteTaxRate(
  id: string,
): Promise<{ id: string; archived: boolean }> {
  const response = await apiFetch<{ id: unknown; archived: boolean }>(
    `/api/tax-rates/${taxRatePathId(id)}`,
    { method: "DELETE" },
  );
  return {
    ...response,
    id: canonicalStockyPublicBigIntId(response.id),
  };
}
